import { useEffect } from "react";
import { usePageText } from "@/hooks/useCms";
import { SEO_PAGES_BY_KEY, SEO_PAGES_DOC_ID, SEO_PAGES } from "@/lib/seoPages";

const SITE_DEFAULT = SEO_PAGES[0];

function setMeta(attr, name, content) {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/** Aplica título/descrição/canonical/og/twitter no <head> (atualiza as tags
 *  existentes — nunca duplica). Ao desmontar repõe os valores do site. */
export function applySeo({ title, description, path }) {
  if (typeof document === "undefined") return;
  if (title) {
    document.title = title;
    setMeta("property", "og:title", title);
    setMeta("name", "twitter:title", title);
  }
  if (description) {
    setMeta("name", "description", description);
    setMeta("property", "og:description", description);
    setMeta("name", "twitter:description", description);
  }
  if (path) {
    const url = `${window.location.origin}${path}`;
    setCanonical(url);
    setMeta("property", "og:url", url);
  }
}

/**
 * SEO de uma página.
 *  - `useSeo({ pageKey: "servicos" })` → usa o Título/Descrição do separador
 *    "Google (SEO)" (ou os defaults de lib/seoPages.js).
 *  - `useSeo({ title, description, path })` → valores diretos (páginas de
 *    detalhe: serviço, solução, notícia).
 *  - `useSeo({ pageKey, title, description })` → `title`/`description` só são
 *    usados se o admin não tiver definido valores para essa página.
 */
export function useSeo({ pageKey, title, description, path, enabled = true } = {}) {
  const { data } = usePageText(SEO_PAGES_DOC_ID, { settings: {} });
  const cfg = pageKey ? SEO_PAGES_BY_KEY[pageKey] : null;
  const stored = (pageKey && data?.settings?.[pageKey]) || {};
  const finalTitle = (stored.title || "").trim() || title || cfg?.title || "";
  const finalDescription = (stored.description || "").trim() || description || cfg?.description || "";
  const finalPath = path || cfg?.path || "";

  useEffect(() => {
    if (!enabled) return undefined;
    applySeo({ title: finalTitle, description: finalDescription, path: finalPath });
    return () => {
      applySeo({ title: SITE_DEFAULT.title, description: SITE_DEFAULT.description, path: "/" });
    };
  }, [enabled, finalTitle, finalDescription, finalPath]);

  return { title: finalTitle, description: finalDescription };
}
